import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBenefitProgramsV1BalanceBalanceServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/pool-accounts',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/benefit-programs-pool-accounts',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-pool-accounts',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/balances',
        destPath: '/v1/balances',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/balances',
        destPath: '/v1/bulk/balances',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-pool-accounts',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/balances',
        destPath: '/v1/balances',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/balances',
        destPath: '/v1/bulk/balances',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/benefit-programs-pool-accounts',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/balances',
        destPath: '/v1/balances',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/balances',
        destPath: '/v1/bulk/balances',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/benefit-programs-pool-accounts',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/balances',
        destPath: '/v1/balances',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/balances',
        destPath: '/v1/bulk/balances',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-pool-accounts',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/balances',
        destPath: '/v1/balances',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/balances',
        destPath: '/v1/bulk/balances',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-pool-accounts',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/balances',
        destPath: '/v1/balances',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/balances',
        destPath: '/v1/bulk/balances',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/benefit-programs/v1/balances',
        destPath: '/v1/balances',
      },
      {
        srcPath: '/benefit-programs/v1/bulk/balances',
        destPath: '/v1/bulk/balances',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/benefit-programs/v1/balances',
        destPath: '/v1/balances',
      },
      {
        srcPath: '/_api/benefit-programs/v1/bulk/balances',
        destPath: '/v1/bulk/balances',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_benefit-programs_balances';

/** Retrieves a balance. */
export function getBalance(payload: object): RequestOptionsFactory<any> {
  function __getBalance({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.balance',
      method: 'GET' as any,
      methodFqn: 'wix.benefit_programs.v1.balance.BalanceService.GetBalance',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1BalanceBalanceServiceUrl({
        protoPath: '/v1/balances/{poolId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'balance.createdDate' },
              { path: 'balance.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getBalance;
}

/** Retrieves a list of balances. */
export function listBalances(payload: object): RequestOptionsFactory<any> {
  function __listBalances({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.balance',
      method: 'GET' as any,
      methodFqn: 'wix.benefit_programs.v1.balance.BalanceService.ListBalances',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1BalanceBalanceServiceUrl({
        protoPath: '/v1/balances',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'balances.createdDate' },
              { path: 'balances.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listBalances;
}

/**
 * Creates a query to retrieve a list of balances.
 *
 * The Query Balances method builds a query to retrieve a list of balances and returns a `BalancesQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `BalancesQueryBuilder` methods onto the query. `BalancesQueryBuilder` methods enable you to filter, sort, and control the results that Query Balances returns.
 *
 * Query Balances has a default paging limit of 50, which you can override.
 *
 *  For a full description of the item object, see the object returned for the `items` property in `BalancesQueryResult`.
 */
export function queryBalances(payload: object): RequestOptionsFactory<any> {
  function __queryBalances({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.balance',
      method: 'POST' as any,
      methodFqn: 'wix.benefit_programs.v1.balance.BalanceService.QueryBalances',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1BalanceBalanceServiceUrl({
        protoPath: '/v1/balances/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'balances.createdDate' },
              { path: 'balances.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryBalances;
}

/**
 * Changes the number of available credits in a balance.
 *
 * You can adjust or set the number of available credits.
 */
export function changeBalance(payload: object): RequestOptionsFactory<any> {
  function __changeBalance({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'transactionDetails.effectiveDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.balance',
      method: 'POST' as any,
      methodFqn: 'wix.benefit_programs.v1.balance.BalanceService.ChangeBalance',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1BalanceBalanceServiceUrl({
        protoPath: '/v1/balances/{poolId}/change',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'balance.createdDate' },
              { path: 'balance.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __changeBalance;
}

/**
 * Reverts a transaction created by calling Change Balance.
 *
 * For example, if a transaction increased a balance's available credits by 5,
 * calling Revert Balance Change with the transaction's ID will decrease the balance's available credit by 5.
 *
 * > **Note:** If the Change Balance call set the balance, Revert Transaction reverts the amount the balance changed by, ensuring subsequent balance changes are still reflected after the reversion.
 */
export function revertBalanceChange(
  payload: object
): RequestOptionsFactory<any> {
  function __revertBalanceChange({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.balance',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.balance.BalanceService.RevertBalanceChange',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1BalanceBalanceServiceUrl({
        protoPath: '/v1/balances/changes/{transactionId}/revert',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __revertBalanceChange;
}
